import { Link, useInRouterContext } from 'react-router-dom';
import { formatRunDate } from '../components/Audit/shared.js';
import { queries } from '../store/queries.js';
import { useStoredQuery } from '../store/StoreProvider.js';
import { color, font, radius } from '../theme/tokens.js';
import { useAlertsSwitchedOff } from './alertsSignal.js';

/**
 * The header bell (blueprint lines 127–130), new to the product with spec 008 (FR-AL02–AL04).
 *
 * - **The same number as the sidebar badge and the unfiltered banner** — `data.total`, recruiters
 *   on alert, never flags (FR-AL03). One response feeds all three.
 * - **It never fetches** (FR-AL04): `useStoredQuery` reads the copy prefetched at sign-in. Before it
 *   lands the badge is ABSENT, never `0` — a zero would claim nobody is flagged.
 * - **It names the run its count describes** (constitution 4.0.0 condition 4: the one place an audit
 *   figure may appear outside the two audit pages must say which run it is).
 * - **Dimmed and unreachable when the feature is off**, like the sidebar entry.
 */
export function AlertsBell() {
  const { data } = useStoredQuery(queries.auditAlerts());
  const switchedOff = useAlertsSwitchedOff();
  const inRouter = useInRouterContext();

  const total = data?.data?.total ?? null;
  const run = data?.data?.clock?.latestRunDate ?? null;
  const name =
    total === null
      ? 'Alerts'
      : `${total} recruiter${total === 1 ? '' : 's'} flagged${run ? ` · audit run ${formatRunDate(run)}` : ''}`;

  const face = (
    <>
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color.inkBody} strokeWidth="1.8" aria-hidden="true">
        <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.7 21a2 2 0 0 1-3.4 0" />
      </svg>
      {total !== null && !switchedOff ? (
        <span
          className="mono"
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: -5,
            right: -5,
            background: color.badgeAlert,
            color: color.onBadgeAlert,
            fontFamily: font.mono,
            fontSize: '9.5px',
            fontWeight: 600,
            padding: '1px 5px',
            borderRadius: radius.pill,
            border: `2px solid ${color.headerBg}`,
          }}
        >
          {total}
        </span>
      ) : null}
    </>
  );

  const style = {
    position: 'relative' as const,
    width: 38,
    height: 38,
    flex: 'none' as const,
    borderRadius: radius['2xl'],
    background: color.surface,
    border: `1px solid ${color.borderStrong}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  if (switchedOff) {
    return (
      <span aria-disabled="true" title="Alerts are switched off" style={{ ...style, opacity: 0.45, pointerEvents: 'none' }}>
        {face}
      </span>
    );
  }
  return inRouter ? (
    <Link to="/alerts" aria-label={name} title={name} style={{ ...style, cursor: 'pointer' }}>
      {face}
    </Link>
  ) : (
    <a href="/alerts" aria-label={name} title={name} style={{ ...style, cursor: 'pointer' }}>
      {face}
    </a>
  );
}
